import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";

import type {
  NotificationBatchOutcome,
  NotificationSend,
  Schema,
} from "@bw/shared/types/generated";

import { PlatformPage } from "@/components/PlatformPage";

import { listRecipients, sendBatch } from "../api";
import { DeliveryRecoveryPanel } from "../components/DeliveryRecoveryPanel";

type PlatformUser = Schema["PlatformUser"];
type Channel = NotificationSend["channel"];
type SubmittedAnnouncement = Readonly<{
  count: number;
  payloads: readonly NotificationSend[];
}>;
type RecipientIdentity = Readonly<{
  accessibleLabel: string;
  detail?: string;
  primary: string;
  selectable: boolean;
  selectedLabel: string;
}>;

const SEND_BATCH_MUTATION_KEY = ["announcements", "send-batch"] as const;

const CHANNELS: ReadonlyArray<{ label: string; value: Channel }> = [
  { label: "In-app", value: "in_app" },
  { label: "Email", value: "email" },
];

function hasEmail(email: PlatformUser["email"]): email is string {
  return typeof email === "string" && email.trim().length > 0;
}

function displayName(recipient: PlatformUser): string | null {
  return recipient.display_name?.trim() || null;
}

function displayNameKey(recipient: PlatformUser): string | null {
  return displayName(recipient)?.toLocaleLowerCase() ?? null;
}

function recipientIdentity(
  recipient: PlatformUser,
  duplicateDisplayNames: ReadonlySet<string>,
): RecipientIdentity {
  const name = displayName(recipient);
  const email = hasEmail(recipient.email) ? recipient.email.trim() : null;
  if (!name && !email) {
    return {
      accessibleLabel: "Identity unavailable",
      primary: "Identity unavailable",
      selectable: false,
      selectedLabel: "Identity unavailable",
    };
  }
  if (!name) {
    return {
      accessibleLabel: email!,
      primary: email!,
      selectable: true,
      selectedLabel: email!,
    };
  }
  if (!duplicateDisplayNames.has(name.toLocaleLowerCase())) {
    return {
      accessibleLabel: name,
      detail: email ?? undefined,
      primary: name,
      selectable: true,
      selectedLabel: name,
    };
  }
  if (!email) {
    return {
      accessibleLabel: `Identity unavailable for ${name}`,
      detail: "Identity unavailable",
      primary: name,
      selectable: false,
      selectedLabel: "Identity unavailable",
    };
  }
  return {
    accessibleLabel: `${name} ${email}`,
    detail: email,
    primary: name,
    selectable: true,
    selectedLabel: `${name} · ${email}`,
  };
}

function submittedLabel(result: NotificationBatchOutcome): string {
  const counts = new Map<string, number>();
  for (const outcome of result.outcomes) {
    counts.set(outcome.disposition, (counts.get(outcome.disposition) ?? 0) + 1);
  }
  const detail = [
    ["persisted", "added to inbox"],
    ["queued", "queued"],
    ["duplicate", "already submitted"],
    ["skipped_deleted", "skipped"],
  ]
    .map(([state, label]) => {
      const count = counts.get(state) ?? 0;
      return count > 0 ? `${count} ${label}` : null;
    })
    .filter((value): value is string => value !== null)
    .join(", ");
  return `Processed ${result.outcomes.length} notification ${
    result.outcomes.length === 1 ? "command" : "commands"
  }: ${detail}.`;
}

function sendLabel(count: number): string {
  return `Send to ${count} ${count === 1 ? "recipient" : "recipients"}`;
}

export function AnnouncementsPage() {
  const queryClient = useQueryClient();
  const sendLock = useRef(false);
  const draftVersion = useRef(0);
  const submittedDraftVersion = useRef<number | null>(null);
  const submissionCommandId = useRef<string | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [channel, setChannel] = useState<Channel>("in_app");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<PlatformUser[]>([]);
  const [submittedResult, setSubmittedResult] =
    useState<NotificationBatchOutcome | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const normalizedSearch = search.trim();

  const recipientsQuery = useQuery({
    queryKey: ["announcements", "recipients", normalizedSearch],
    queryFn: () =>
      listRecipients(
        normalizedSearch ? { search: normalizedSearch } : undefined,
      ),
  });

  const globalSendCount = useIsMutating({
    mutationKey: SEND_BATCH_MUTATION_KEY,
  });

  function resetComposer() {
    setTitle("");
    setBody("");
    setChannel("in_app");
    setSelected([]);
  }

  const sendMutation = useMutation({
    mutationKey: SEND_BATCH_MUTATION_KEY,
    mutationFn: (variables: SubmittedAnnouncement) =>
      sendBatch(variables.payloads),
    retry: false,
    onSuccess: (response) => {
      if (submittedDraftVersion.current !== draftVersion.current) return;
      setSubmittedResult(response);
      setSendError(null);
      resetComposer();
      submissionCommandId.current = null;
      draftVersion.current += 1;
    },
    onError: () => {
      if (submittedDraftVersion.current !== draftVersion.current) return;
      setSendError(
        "We couldn't confirm the submission outcome. Some recipients may already have received it.",
      );
    },
    onSettled: () => {
      sendLock.current = false;
    },
  });

  const submissionPending = sendMutation.isPending || globalSendCount > 0;
  const recipients = recipientsQuery.data?.items ?? [];
  const duplicateDisplayNames = useMemo(() => {
    const uidsByName = new Map<string, Set<string>>();
    for (const recipient of [...selected, ...recipients]) {
      const key = displayNameKey(recipient);
      if (!key) continue;
      const uids = uidsByName.get(key) ?? new Set<string>();
      uids.add(recipient.uid);
      uidsByName.set(key, uids);
    }
    return new Set(
      [...uidsByName.entries()]
        .filter(([, uids]) => uids.size > 1)
        .map(([name]) => name),
    );
  }, [recipients, selected]);
  const safelyIdentifiedSelected = useMemo(
    () =>
      selected.filter(
        (recipient) =>
          recipientIdentity(recipient, duplicateDisplayNames).selectable,
      ),
    [duplicateDisplayNames, selected],
  );
  const eligibleRecipients = useMemo(
    () =>
      channel === "email"
        ? safelyIdentifiedSelected.filter((recipient) =>
            hasEmail(recipient.email),
          )
        : safelyIdentifiedSelected,
    [channel, safelyIdentifiedSelected],
  );
  const noEmailCount =
    channel === "email" ? selected.length - eligibleRecipients.length : 0;
  const canSend =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    eligibleRecipients.length > 0 &&
    !submissionPending;

  function markDraftChanged() {
    draftVersion.current += 1;
    submissionCommandId.current = null;
    setSubmittedResult(null);
    setSendError(null);
  }

  function selectRecipient(recipient: PlatformUser) {
    if (
      submissionPending ||
      !recipientIdentity(recipient, duplicateDisplayNames).selectable ||
      selected.some((candidate) => candidate.uid === recipient.uid)
    ) {
      return;
    }
    markDraftChanged();
    setSelected((current) => [...current, recipient]);
  }

  function removeRecipient(uid: string) {
    if (submissionPending) return;
    markDraftChanged();
    setSelected((current) =>
      current.filter((recipient) => recipient.uid !== uid),
    );
  }

  function buildPayloads(commandId: string): readonly NotificationSend[] {
    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();
    return eligibleRecipients.map((recipient, position) => {
      const idempotencyKey = `platform-announcement:${commandId}:${position}`;
      if (channel === "email" && hasEmail(recipient.email)) {
        return {
          user_id: recipient.uid,
          title: trimmedTitle,
          body: trimmedBody,
          channel: "email",
          notification_type: "general",
          idempotency_key: idempotencyKey,
        };
      }
      return {
        user_id: recipient.uid,
        title: trimmedTitle,
        body: trimmedBody,
        channel,
        notification_type: "general",
        idempotency_key: idempotencyKey,
      };
    });
  }

  function submitAnnouncement() {
    if (
      !canSend ||
      sendLock.current ||
      queryClient.isMutating({
        mutationKey: SEND_BATCH_MUTATION_KEY,
        exact: true,
      }) > 0
    ) {
      return;
    }

    const commandId =
      submissionCommandId.current ??
      (submissionCommandId.current = crypto.randomUUID());
    const payloads = buildPayloads(commandId);
    const variables: SubmittedAnnouncement = {
      count: payloads.length,
      payloads,
    };
    sendLock.current = true;
    submittedDraftVersion.current = draftVersion.current;
    setSubmittedResult(null);
    setSendError(null);
    sendMutation.mutate(variables);
  }

  return (
    <PlatformPage title="Announcements">
      <Stack spacing={2.5}>
        <Box aria-live="polite">
          {submittedResult !== null && (
            <Alert severity="success">{submittedLabel(submittedResult)}</Alert>
          )}
        </Box>

        {sendError && (
          <Alert severity="error" role="alert">
            {sendError}
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              lg: "minmax(0, 3fr) minmax(320px, 2fr)",
            },
            gap: 2.5,
            alignItems: "start",
          }}
        >
          <Paper
            component="section"
            aria-label="Announcement composer"
            variant="outlined"
            sx={{ p: { xs: 2, md: 2.5 } }}
          >
            <Stack spacing={2}>
              <TextField
                label="Title"
                size="small"
                fullWidth
                value={title}
                disabled={submissionPending}
                onChange={(event) => {
                  markDraftChanged();
                  setTitle(event.target.value);
                }}
              />

              <TextField
                label="Body"
                size="small"
                fullWidth
                multiline
                minRows={6}
                value={body}
                disabled={submissionPending}
                onChange={(event) => {
                  markDraftChanged();
                  setBody(event.target.value);
                }}
              />

              <FormControl component="fieldset" disabled={submissionPending}>
                <FormLabel component="legend">Channel</FormLabel>
                <RadioGroup
                  row
                  name="announcement-channel"
                  value={channel}
                  onChange={(event) => {
                    markDraftChanged();
                    setChannel(event.target.value as Channel);
                  }}
                  sx={{ columnGap: 2 }}
                >
                  {CHANNELS.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio size="small" />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>

              {channel === "email" && noEmailCount > 0 && (
                <Alert severity="warning" variant="outlined">
                  {noEmailCount} of {selected.length} selected have no email and
                  will be skipped.
                </Alert>
              )}

              <Button
                type="button"
                size="small"
                variant="contained"
                disabled={!canSend}
                onClick={submitAnnouncement}
                sx={{
                  alignSelf: "flex-start",
                  "&:disabled": { pointerEvents: "auto" },
                }}
              >
                {submissionPending
                  ? "Submitting..."
                  : sendLabel(eligibleRecipients.length)}
              </Button>
            </Stack>
          </Paper>

          <Paper
            component="section"
            aria-labelledby="announcement-recipients-heading"
            variant="outlined"
            sx={{ p: { xs: 2, md: 2.5 }, minWidth: 0 }}
          >
            <Stack spacing={2}>
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ alignItems: "center", justifyContent: "space-between" }}
              >
                <Typography
                  id="announcement-recipients-heading"
                  component="h2"
                  variant="h6"
                  sx={{ fontWeight: 700 }}
                >
                  Recipients
                </Typography>
                <Chip
                  size="small"
                  label={`${selected.length} selected`}
                  variant="outlined"
                />
              </Stack>

              {selected.length > 0 && (
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  sx={{ flexWrap: "wrap" }}
                >
                  {selected.map((recipient) => {
                    const identity = recipientIdentity(
                      recipient,
                      duplicateDisplayNames,
                    );
                    return (
                      <Chip
                        key={recipient.uid}
                        component="button"
                        type="button"
                        size="small"
                        variant="outlined"
                        label={identity.selectedLabel}
                        aria-label={`Remove ${identity.accessibleLabel}`}
                        disabled={submissionPending}
                        onClick={() => removeRecipient(recipient.uid)}
                        onDelete={() => removeRecipient(recipient.uid)}
                      />
                    );
                  })}
                </Stack>
              )}

              <TextField
                type="search"
                size="small"
                fullWidth
                label="Search users"
                value={search}
                disabled={submissionPending}
                onChange={(event) => setSearch(event.target.value)}
                slotProps={{
                  htmlInput: { "aria-label": "Search users" },
                }}
              />

              {recipientsQuery.isPending ? (
                <Box
                  role="status"
                  aria-label="Loading users"
                  aria-live="polite"
                  sx={{ display: "flex", justifyContent: "center", py: 5 }}
                >
                  <CircularProgress size={30} />
                </Box>
              ) : recipientsQuery.isError ? (
                <Alert
                  severity="error"
                  role="alert"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      disabled={
                        recipientsQuery.fetchStatus !== "idle" ||
                        submissionPending
                      }
                      onClick={() => void recipientsQuery.refetch()}
                    >
                      Retry
                    </Button>
                  }
                >
                  Couldn't load users. Please try again.
                </Alert>
              ) : recipients.length === 0 ? (
                <Typography
                  color="text.secondary"
                  role="status"
                  sx={{ py: 4, textAlign: "center" }}
                >
                  No users match.
                </Typography>
              ) : (
                <Stack
                  component="ul"
                  spacing={0.75}
                  sx={{
                    listStyle: "none",
                    m: 0,
                    p: 0,
                    maxHeight: 360,
                    overflowY: "auto",
                  }}
                >
                  {recipients.map((recipient) => {
                    const identity = recipientIdentity(
                      recipient,
                      duplicateDisplayNames,
                    );
                    const selectedAlready = selected.some(
                      (candidate) => candidate.uid === recipient.uid,
                    );
                    return (
                      <Box component="li" key={recipient.uid}>
                        <Button
                          type="button"
                          fullWidth
                          variant="outlined"
                          aria-label={
                            identity.selectable
                              ? `Add ${identity.accessibleLabel}`
                              : identity.accessibleLabel
                          }
                          disabled={
                            !identity.selectable ||
                            selectedAlready ||
                            submissionPending
                          }
                          onClick={() => selectRecipient(recipient)}
                          sx={{
                            justifyContent: "space-between",
                            px: 1.5,
                            py: 1,
                            textAlign: "left",
                            textTransform: "none",
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              component="span"
                              variant="body2"
                              noWrap
                              sx={{ display: "block", fontWeight: 600 }}
                            >
                              {identity.primary}
                            </Typography>
                            {identity.detail && (
                              <Typography
                                component="span"
                                color="text.secondary"
                                variant="caption"
                                noWrap
                                sx={{ display: "block" }}
                              >
                                {identity.detail}
                              </Typography>
                            )}
                          </Box>
                          <Typography
                            component="span"
                            color="text.secondary"
                            variant="caption"
                            sx={{ ml: 1.5, flexShrink: 0 }}
                          >
                            {!identity.selectable
                              ? "Unavailable"
                              : selectedAlready
                                ? "Added"
                                : "Add"}
                          </Typography>
                        </Button>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          </Paper>
        </Box>

        <DeliveryRecoveryPanel />
      </Stack>
    </PlatformPage>
  );
}
